import { Button, Checkbox, Dropdown, Menu, Popconfirm, Tag, Tooltip } from 'antd';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    FileDoneOutlined,
    FileExclamationOutlined,
    FileOutlined,
    QuestionCircleOutlined,
    SyncOutlined
} from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import { Observer, observer } from 'mobx-react-lite';
import * as React from 'react';
import { ViewerPagesNavigation } from '../../../viewer_base/routes';
import { HasPermission } from '../../../authorization/components/HasPermission';
import { AppPermissions } from '../../../authorization/Permissions';
import { FeatureFlags, Package, PackageState } from '../../../common/models';
import { UploadedPackagesVisualStore } from '../../stores';
import PackageDownloadMenu from '../PackageDownloadMenu';
import PackageTagsEditor from '../PackageTagsEditor';
import { areFeatureFlagsEqual } from '../../../common/misc/FeatureFlagsHelpers';
import { Utils } from '../../../common/services/Utils';
import { SorterResult } from 'antd/lib/table/interface';
import FeatureFlagsModal from '../FeatureFlagsModal';
import { LIST_PAGE_SIZE } from '../../../common/stores/Pager';
import VirtualTable from '../../../common/components/VirtualTable';
import { OrderBy } from '../../../common/types';
import { useDynamicHeight } from '../../../common/hooks';
import './UploadedPackagesGridFc.less';
import { packageProcessingPresentation } from './packageProcessingPresentation';

type Props = {
    store: UploadedPackagesVisualStore;
};

const UploadedPackagesGridFc: React.FC<Props> = ({ store }) => {
    const [menuPlacement, setMenuPlacement] = React.useState<'bottomRight' | 'topRight'>('bottomRight');
    const [featureFlagsDialogVisible, setFeatureFlagsDialogVisible] = React.useState(false);
    const [currentFeatureFlags, setCurrentFeatureFlags] = React.useState<FeatureFlags>(new FeatureFlags());
    const [currentPackageId, setCurrentPackageId] = React.useState<string>('');

    React.useEffect(() => {
        const packagesInSameProject =
            store.currentProjectPackages.length > 0 &&
            store.currentProjectPackages.every(p => p.project.id === store.currentProject?.id);

        if (store.currentProject && !packagesInSameProject) {
            store.setCurrentPage(store.currentPage + 1);
            store.searchForAutocomplete(store.searchTerm);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store.currentProject]);

    const processMenuRenderer = (pkg: Package) => {
        return (
            <Menu>
                <Menu.Item
                    key="1"
                    eventKey="1"
                    disabled={store.isPackageProcessing(pkg.id)}
                    onClick={() => {
                        store.reparsePackage(pkg.id);
                    }}
                >
                    <Tooltip title="Repeat parsing/OCR and preview generation, then run subsequent processing.">
                        <span>Reparse document</span>
                    </Tooltip>
                </Menu.Item>
                <Menu.Item
                    key="run-interpretation"
                    eventKey="run-interpretation"
                    disabled={!store.canRunInterpretation(pkg.id)}
                    onClick={() => store.runInterpretation(pkg.id)}
                >
                    <Tooltip
                        title={
                            store.isPackageProcessing(pkg.id)
                                ? 'Processing is already in progress.'
                                : !store.canRunInterpretation(pkg.id)
                                  ? packageProcessingPresentation(pkg, store.hasVerifiedPreparedSource(pkg.id))
                                        .sourceDescription
                                  : 'Reuse OCR and previews; rerun subsequent processing, including configured extraction, ACORD, indexing and rules.'
                        }
                    >
                        <span>Reprocess document</span>
                    </Tooltip>
                </Menu.Item>
                <Menu.Item
                    key="2"
                    eventKey="2"
                    disabled={
                        store.isPackageProcessing(pkg.id) ||
                        Boolean(pkg.preparedSource && pkg.preparedSourceMatchesSettings !== true)
                    }
                    onClick={() => {
                        store.reindexPackage(pkg.id);
                    }}
                >
                    Re-index
                </Menu.Item>
                <HasPermission
                    entityId={store?.currentProject?.id}
                    permissionClaim={AppPermissions.CanAccessAdministration}
                >
                    <Menu.Item
                        key="3"
                        eventKey="3"
                        onClick={() => {
                            store.updatePackagesProtection([pkg.id], !pkg.isProtected);
                        }}
                    >
                        {pkg.isProtected ? 'Disable protection' : 'Enable protection'}
                    </Menu.Item>
                </HasPermission>
                <Popconfirm
                    title="Are you sure that you want to delete this package?"
                    id={`data-id-popconfirm-box-Delete-${pkg.id}`}
                    disabled={pkg.isProtected}
                    okText="Yes"
                    onConfirm={() => {
                        store.deletePackage(pkg.id);
                    }}
                    cancelText="No"
                >
                    <Menu.Item style={{ color: pkg.isProtected ? 'lightgray' : 'red' }} key="4" eventKey="4">
                        Delete
                    </Menu.Item>
                </Popconfirm>
            </Menu>
        );
    };

    const handleProcessClick = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        if (window.innerHeight - e.clientY < 150) {
            setMenuPlacement('topRight');
        } else {
            setMenuPlacement('bottomRight');
        }
    };

    const actionsCellRenderer = (pkgName: string, id: string) => {
        const pkg = store.currentProjectPackages.find(p => p.id === id);
        if (!pkg) {
            return null;
        }
        return (
            <HasPermission
                entityId={store?.currentProject?.id}
                permissionClaim={AppPermissions.CanEditProcessDocuments}
            >
                <div data-id={`Actions-${pkgName}`}>
                    <PackageDownloadMenu pkg={pkg} />
                    <Observer>
                        {() => (
                            <Dropdown
                                placement={menuPlacement}
                                overlay={() => processMenuRenderer(pkg)}
                                trigger={['click']}
                            >
                                <Tooltip title="Process">
                                    <Button
                                        data-id={`uploaded-packages-reindex-${id}`}
                                        type="link"
                                        size="small"
                                        loading={store.isReindexingInProcess[id]}
                                        onClick={handleProcessClick}
                                    >
                                        <i className="alpha-icon lg play-icon" />
                                    </Button>
                                </Tooltip>
                            </Dropdown>
                        )}
                    </Observer>
                    <Observer>
                        {() => (
                            <Tooltip title="Clean up">
                                <Button
                                    data-id={`uploaded-packages-cleanup-${id}`}
                                    type="link"
                                    size="small"
                                    onClick={() => {
                                        store.cleanStorage(id);
                                    }}
                                    loading={store.isCleanupInProcess[id]}
                                >
                                    <i className="alpha-icon lg cleanup-icon" />
                                </Button>
                            </Tooltip>
                        )}
                    </Observer>
                </div>
            </HasPermission>
        );
    };

    const getSorterOrder = (columnKey: string) => {
        if (!store.orderBy || store.orderBy.field !== columnKey) {
            return;
        }

        return store.orderBy.direction === 'ASC' ? 'ascend' : 'descend';
    };

    const columns: ColumnsType<Package> = [
        {
            key: 'selection',
            dataIndex: 'selection',
            width: 45,
            render: (name: string, record: Package) => (
                <Observer>
                    {() => (
                        <div className="no-overflow-ellipsis">
                            <Checkbox
                                data-id={`CheckBox-${record.name}`}
                                checked={store.selectedPackages.includes(record.id)}
                                data-package-id={record.id}
                                style={{ marginTop: -3 }}
                                onChange={e => store.handlePackageSelection(e, record.id)}
                            />
                        </div>
                    )}
                </Observer>
            )
        },
        {
            key: '_id',
            dataIndex: 'id',
            title: 'Id',
            sorter: true,
            sortOrder: getSorterOrder('_id'),
            render: (name: string, record: Package) => <div data-id={`Id-${record.id}`}>{record.id}</div>
        },
        {
            key: 'fileName',
            dataIndex: 'name',
            title: 'Package',
            sorter: true,
            sortOrder: getSorterOrder('fileName'),
            render: (name: string, record: Package) =>
                !record.documentPath ? (
                    <div data-id={`Package-${record.name}`}>{record.name}</div>
                ) : (
                    <div>
                        <a
                            style={{ whiteSpace: 'pre' }}
                            data-id={`Package-${record.name}`}
                            href={`${process.env.PUBLIC_URL}${ViewerPagesNavigation.DocumentViewerPage}/${encodeURIComponent(
                                record.id
                            )}?fileName=${encodeURIComponent(record.name)}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{record.name}</span>}>
                                {record.name}
                            </Tooltip>
                        </a>
                    </div>
                )
        },
        {
            key: 'filePath',
            dataIndex: 'documentPath',
            title: 'Path',
            sorter: true,
            sortOrder: getSorterOrder('filePath'),
            render: (path: string, record: Package) => (
                <Tooltip title={path}>
                    <div data-id={`Path-${record.name}`}>{path}</div>
                </Tooltip>
            )
        },
        {
            key: 'state',
            dataIndex: 'serverState',
            title: 'State',
            width: 75,
            sorter: true,
            sortOrder: getSorterOrder('state'),
            render: (state: string, record: Package) => {
                const error = packageProcessingPresentation(
                    record,
                    store.hasVerifiedPreparedSource(record.id)
                ).processingDescription;
                const tag = (
                    <Tag
                        className="alpha-portal-tag"
                        data-id={`State-${record.name}`}
                        tabIndex={state === PackageState.Broken ? 0 : undefined}
                        color={state === PackageState.Ready ? '#52C41A' : '#F5222D'}
                        style={{ textTransform: 'capitalize' }}
                    >
                        {state}
                    </Tag>
                );

                if (state === PackageState.Broken) {
                    return (
                        <Tooltip
                            overlayStyle={{ maxWidth: '500px' }}
                            title={<span className="package-processing-tooltip">{error}</span>}
                            trigger={['hover', 'focus']}
                        >
                            {tag}
                        </Tooltip>
                    );
                }

                return tag;
            }
        },
        {
            key: 'processingStage',
            title: (
                <span className="package-processing-heading">
                    Processing
                    <Tooltip
                        trigger={['hover', 'focus', 'click']}
                        overlayStyle={{ maxWidth: '450px' }}
                        title={
                            <div className="package-processing-legend">
                                <p>
                                    <CheckCircleOutlined /> <strong>Left — processing status:</strong> the current step
                                    or the result of the latest run. Green means processing completed and the package is
                                    Ready.
                                </p>
                                <p>
                                    <FileDoneOutlined /> <strong>Right — OCR and previews:</strong> whether first-stage
                                    data can be reused. Green does not mean all processing has completed. Amber means
                                    previous data is retained but a reparse is required; gray means data is being
                                    prepared or reuse has not been verified.
                                </p>
                                <p>
                                    <strong>Preparation:</strong> parsing/OCR and preview generation.
                                </p>
                                <p>
                                    <strong>Subsequent processing:</strong> configured extraction, Smart Blocks,
                                    Interpretation Blocks/custom pipelines, ACORD/rates processing, indexing and rules,
                                    as applicable. A custom pipeline is not required for these other steps.
                                </p>
                                <p>Hover over or focus either row icon for its status and any error details.</p>
                            </div>
                        }
                    >
                        <button type="button" className="package-processing-help" aria-label="Processing icon guide">
                            <QuestionCircleOutlined />
                        </button>
                    </Tooltip>
                </span>
            ),
            width: 105,
            render: (_: unknown, record: Package) => {
                const sourceAvailable = store.hasVerifiedPreparedSource(record.id);
                const {
                    active,
                    failed,
                    complete,
                    processingDescription: stageDescription,
                    sourceDescription,
                    sourceClass
                } = packageProcessingPresentation(
                    record,
                    sourceAvailable,
                    Boolean(store.isStartingPreparation?.[record.id])
                );
                return (
                    <div className="package-processing-icons" data-id={`Processing-${record.id}`}>
                        <Tooltip
                            title={<span className="package-processing-tooltip">{stageDescription}</span>}
                            trigger={['hover', 'focus']}
                            overlayStyle={{ maxWidth: '500px' }}
                        >
                            <span
                                role="img"
                                aria-label={stageDescription}
                                tabIndex={0}
                                className={`package-processing-icon ${active ? 'processing' : failed ? 'failed' : complete ? 'complete' : 'pending'}`}
                            >
                                {active ? (
                                    <SyncOutlined spin aria-hidden="true" />
                                ) : failed ? (
                                    <CloseCircleOutlined aria-hidden="true" />
                                ) : complete ? (
                                    <CheckCircleOutlined aria-hidden="true" />
                                ) : (
                                    <ClockCircleOutlined aria-hidden="true" />
                                )}
                            </span>
                        </Tooltip>
                        <Tooltip title={sourceDescription} trigger={['hover', 'focus']}>
                            <span
                                role="img"
                                aria-label={sourceDescription}
                                tabIndex={0}
                                className={`package-processing-icon ${sourceClass}`}
                            >
                                {sourceAvailable ? (
                                    <FileDoneOutlined aria-hidden="true" />
                                ) : sourceClass === 'unavailable' ? (
                                    <FileExclamationOutlined aria-hidden="true" />
                                ) : (
                                    <FileOutlined aria-hidden="true" />
                                )}
                            </span>
                        </Tooltip>
                    </div>
                );
            }
        },
        {
            key: 'userTags',
            title: 'Tags',
            width: 150,
            sorter: true,
            sortOrder: getSorterOrder('userTags'),
            render: (val: unknown, record: Package) => (
                <div data-id={`Tags-${record.name}`}>
                    <PackageTagsEditor store={store} line={record} />
                </div>
            )
        },
        {
            key: 'featureFlags',
            dataIndex: 'featureFlags',
            title: 'Flags',
            sorter: true,
            sortOrder: getSorterOrder('featureFlags'),
            render: (__: FeatureFlags, record: Package) => (
                <Observer>
                    {() => (
                        <Button
                            type="link"
                            size="small"
                            onClick={() => openFeatureFlagsModal(record)}
                            data-id={`Flags-${record.name}`}
                        >
                            {/* Compare locally because rows come from both search results and package events.
                                Keep equality rules aligned with server-side Flags sorting. */}
                            {areFeatureFlagsEqual(record.featureFlags, store.currentProjectFeatureFlags)
                                ? 'Matches project'
                                : 'Differs from project'}
                        </Button>
                    )}
                </Observer>
            )
        },
        {
            key: 'uploadedTime',
            dataIndex: 'uploadDate',
            title: 'Upload Date',
            width: 155,
            sorter: true,
            sortOrder: getSorterOrder('uploadedTime'),
            render: (uploadDate: string, record: Package) => (
                <span data-id={`UploadDate-${record.name}`} data-id-timestamp={uploadDate}>
                    {Utils.formatDateStringShort(uploadDate, true)}
                </span>
            )
        },
        {
            key: 'indexDate',
            dataIndex: 'indexDate',
            title: 'Index date',
            width: 155,
            sorter: true,
            sortOrder: getSorterOrder('indexDate'),
            render: (indexDate: string, record: Package) => (
                <div className="overlaying-row-actions">
                    <span data-id={`IndexDate-${record.name}`} className="cell-text" data-id-timestamp={indexDate}>
                        {Utils.formatDateStringShort(indexDate, true)}
                    </span>
                    <span className="cell-actions">{actionsCellRenderer(record.name, record.id)}</span>
                </div>
            )
        }
    ];

    const openFeatureFlagsModal = (record: Package) => {
        setCurrentFeatureFlags(record.featureFlags);
        setCurrentPackageId(record.id);
        setFeatureFlagsDialogVisible(true);
    };

    const getHighlightedRowStyle = React.useCallback(() => ({ backgroundColor: '#E4E5ED' }), []);

    const containerRef = React.useRef<HTMLDivElement>(null);

    const { height } = useDynamicHeight(containerRef, 120);

    return (
        <div ref={containerRef}>
            {featureFlagsDialogVisible && (
                <FeatureFlagsModal
                    currentFeatureFlags={currentFeatureFlags}
                    changeVisible={setFeatureFlagsDialogVisible}
                    store={store}
                    visible={featureFlagsDialogVisible}
                    currentPackageId={currentPackageId}
                />
            )}
            <VirtualTable
                data-id="table-packages-list"
                columns={columns}
                dataSource={store.currentProjectPackages}
                highlightedRowsIndexes={store.protectedPackageIndexes}
                getHighlightedRowStyle={getHighlightedRowStyle}
                scroll={{ y: height }}
                className="uploaded-packages-table"
                loading={store.isLoading}
                pagination={{
                    pageSize: store.pageSize,
                    hideOnSinglePage: false,
                    pageSizeOptions: [LIST_PAGE_SIZE.toString(), '100', '150', '200', '250'],
                    total: store.totalPackagesInProject,
                    onChange: (page: number, pageSize?: number) => {
                        store.setPageSize(pageSize);
                        store.setCurrentPage(page);
                    },
                    current: store.currentPage + 1
                }}
                rowSelection={{
                    hideSelectAll: false,
                    onChange: store.handleSelection
                }}
                onChange={(pagination, _, sorter: SorterResult<Package>) => {
                    let orderBy: OrderBy | undefined;

                    if (sorter.columnKey && sorter.order) {
                        orderBy = {
                            field: sorter.columnKey as string,
                            direction: sorter.order === 'ascend' ? 'ASC' : 'DESC'
                        };
                    }

                    store.setOrderBy(orderBy);
                    store.setPageSize(pagination.pageSize);
                    store.loadProjectPackages(store.searchTerm.trim());
                }}
            />
        </div>
    );
};

export default observer(UploadedPackagesGridFc);
